import jsPDF from 'jspdf';
import ChenthoorLogo from '../../../assets/images/Chenthoor_landscape_Logo-removebg-preview.png';
import axios from '../../../axios-url';

// Standalone copy of the Position Master "PDF" output, used only by the Employee Master
// "Generate Contract" flow. It mirrors AddPosition.buildPdf but works from the getposition
// response instead of component state, so the Position Master page is left untouched.

const looksLikeHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(value || '');

// Editor HTML -> plain text with line breaks and list markers
const htmlToPlainText = (value) => {
    if (!value) { return ''; }
    if (!looksLikeHtml(value)) { return String(value); }
    const container = document.createElement('div');
    container.innerHTML = value;
    const lines = [];
    container.childNodes.forEach(node => {
        if (node.nodeName === 'UL' || node.nodeName === 'OL') {
            Array.from(node.children).forEach((li, i) => {
                lines.push((node.nodeName === 'OL' ? (i + 1) + '. ' : '- ') + li.textContent);
            });
        } else {
            lines.push(node.textContent);
        }
    });
    return lines.join('\n').replace(/ /g, ' ').trim();
};

const loadLogo = () => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL('image/png'), ratio: img.naturalWidth / img.naturalHeight });
    };
    img.onerror = () => resolve(null);
    img.src = ChenthoorLogo;
});

const fetchOptions = (keyword) => axios.get(`/master/api/DropDownValues/${keyword}`)
    .then(res => (Array.isArray(res && res.data) ? res.data : []))
    .catch(() => []);

const labelOfKeyword = (list, id) => {
    if (id === undefined || id === null || id === '') return '';
    const idStr = String(id).trim().toLowerCase();
    const item = list.find(entry => [entry.id, entry.uniqueId, entry.value]
        .some(c => c !== undefined && c !== null && String(c).trim().toLowerCase() === idStr));
    return item ? (item.text || item.label || '') : '';
};

// Fetches the position (same endpoint as the Position Master edit page) plus the lookups needed
// for labels, and returns { blob, fileName } for the Position PDF. Resolves null if no position.
export const generatePositionPdf = (positionId) => {
    if (!positionId) return Promise.resolve(null);

    return Promise.all([
        axios.get(`/master/api/getposition?Id=${positionId}`).then(r => r.data).catch(() => null),
        fetchOptions('Job'),
        fetchOptions('resRolesandResponsibility'),
        axios.get('/master/api/GetPositions').then(r => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
        loadLogo(),
    ]).then(([body, jobs, responsibilityOptions, positions, logo]) => {
        if (!body) return null;
        const record = (Array.isArray(body.positionList) && body.positionList.length > 0)
            ? body.positionList[0]
            : (body.position || (body.id !== undefined ? body : null));
        if (!record) return null;

        const resList = record.resRolesResponsibilitiesList !== undefined ? record.resRolesResponsibilitiesList : body.resRolesResponsibilitiesList;
        const rawRoles = record.positionRoles !== undefined ? record.positionRoles : body.positionRoles;
        const resRolesList = Array.isArray(resList) ? resList : [];
        const recordSopTamil = record.soptamil ?? body.soptamil ?? '';
        const recordSopEnglish = record.sopenglish ?? body.sopenglish ?? '';

        const positionRoles = (rawRoles && rawRoles.length > 0)
            ? rawRoles.map(role => ({
                Responsibilities: ((role.Responsibilities || role.responsibilities) || []).map(res => ({
                    ResponsibilityId: res.ResponsibilityId ?? res.responsibilityId ?? null,
                    SopTamil: res.SopTamil, SopEnglish: res.SopEnglish,
                })),
            }))
            : (resRolesList.length > 0
                ? [{ Responsibilities: resRolesList.map(r => ({ ResponsibilityId: r.roleResponsibilityId ?? null })) }]
                : []);

        const state = {
            Name: record.name ?? '',
            PositionNo: record.positionNo ?? '',
            Description: record.description ?? '',
            JobId: record.jobId ?? null,
            ReportedTo: record.reportedTo ?? null,
            IsActive: record.isactive ?? null,
            positionRoles,
        };

        const getResponsibilityText = (res, field, existingKey) => {
            if (res[field] !== undefined) { return res[field]; }
            const existing = resRolesList.find(r => r.roleResponsibilityId === res.ResponsibilityId);
            const recordValue = (existingKey === 'soptamil') ? recordSopTamil : recordSopEnglish;
            return (existing && existing[existingKey]) || recordValue || '';
        };

        const reportingTo = (positions.find(p => String(p.positionId) === String(state.ReportedTo)) || {}).name || '';
        const jobLabel = labelOfKeyword(jobs, state.JobId);

        return buildPdf({
            state, logo, jobLabel, reportingTo,
            responsibilityLabel: (id) => labelOfKeyword(responsibilityOptions, id),
            getResponsibilityText,
        });
    });
};

const buildPdf = ({ state, logo, jobLabel, reportingTo, responsibilityLabel, getResponsibilityText }) => {
    const TEAL = [12, 107, 122];
    const GOLD = [242, 194, 27];
    const DARK = [33, 37, 41];
    const MUTED = [108, 117, 125];
    const CARD = [243, 248, 249];

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const bottomLimit = pageHeight - 20;
    const lineHeight = 4.8;
    let y = 0;

    // Wrapped text rendered on a canvas so the browser shapes Tamil correctly
    const tamilTextImage = (text, widthMm, lineMm) => {
        const scale = 12;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const fontPx = lineMm * scale * 0.72;
        const font = fontPx + 'px "Noto Sans Tamil", "Nirmala UI", Latha, Catamaran, sans-serif';
        const widthPx = Math.round(widthMm * scale);
        ctx.font = font;

        const lines = [];
        text.split(/\r?\n/).forEach(paragraph => {
            let current = '';
            paragraph.split(/\s+/).forEach(word => {
                const test = current ? current + ' ' + word : word;
                if (current && ctx.measureText(test).width > widthPx) {
                    lines.push(current);
                    current = word;
                } else {
                    current = test;
                }
            });
            lines.push(current);
        });

        canvas.width = widthPx;
        canvas.height = Math.round(lines.length * lineMm * scale);
        ctx.font = font;
        ctx.fillStyle = '#212529';
        ctx.textBaseline = 'middle';
        lines.forEach((line, i) => ctx.fillText(line, 0, (i + 0.5) * lineMm * scale));
        return { data: canvas.toDataURL('image/png'), height: lines.length * lineMm };
    };

    const setColor = (c) => doc.setTextColor(c[0], c[1], c[2]);
    const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);

    setFill(TEAL);
    doc.rect(0, 0, pageWidth, 34, 'F');
    setFill(GOLD);
    doc.rect(0, 34, pageWidth, 1.5, 'F');
    if (logo) {
        const logoHeight = 22;
        const logoWidth = logoHeight * logo.ratio;
        doc.addImage(logo.data, 'PNG', (pageWidth - logoWidth) / 2, 6, logoWidth, logoHeight);
    }
    y = 46;

    const newPage = () => {
        doc.addPage();
        setFill(TEAL);
        doc.rect(0, 0, pageWidth, 8, 'F');
        setFill(GOLD);
        doc.rect(0, 8, pageWidth, 0.8, 'F');
        y = 18;
    };

    const ensureSpace = (height) => {
        if (y + height > bottomLimit) { newPage(); }
    };

    const sectionHeading = (title) => {
        ensureSpace(14);
        setFill(GOLD);
        doc.rect(margin, y - 4.5, 1.8, 6.5, 'F');
        setColor(TEAL);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(title.toUpperCase(), margin + 4.5, y);
        y += 3;
        doc.setDrawColor(220, 226, 229);
        doc.line(margin, y, pageWidth - margin, y);
        y += 7;
    };

    const field = (label, value, x, width) => {
        setColor(MUTED);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(label.toUpperCase(), x, y);
        setColor(DARK);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10.5);
        const lines = doc.splitTextToSize(String(value || '-'), width);
        doc.text(lines, x, y + 5);
        return 5 + lines.length * lineHeight;
    };

    // Title card
    const hasStatus = state.IsActive !== null && state.IsActive !== undefined;
    const isActive = state.IsActive === true;
    setFill(CARD);
    doc.roundedRect(margin, y - 6, contentWidth, 22, 2, 2, 'F');
    setFill(TEAL);
    doc.rect(margin, y - 6, 2.2, 22, 'F');
    setColor(TEAL);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    const titleLines = doc.splitTextToSize(state.Name || '-', contentWidth - 50);
    doc.text(titleLines[0], margin + 7, y + 7);
    if (hasStatus) {
        const badgeWidth = 24;
        setFill(isActive ? [40, 167, 69] : [220, 53, 69]);
        doc.roundedRect(pageWidth - margin - badgeWidth - 5, y + 1, badgeWidth, 8, 4, 4, 'F');
        setColor([255, 255, 255]);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(isActive ? 'ACTIVE' : 'INACTIVE', pageWidth - margin - badgeWidth / 2 - 5, y + 6.2, { align: 'center' });
    }
    y += 26;

    sectionHeading('Position Details');
    const colWidth = (contentWidth - 10) / 2;
    const col2 = margin + colWidth + 10;

    const h2 = Math.max(field('Job', jobLabel, margin, colWidth), field('Reporting To', reportingTo, col2, colWidth));
    y += h2 + 5;
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'normal');
    const descLines = doc.splitTextToSize(String(state.Description || '-'), contentWidth);
    ensureSpace(5 + descLines.length * lineHeight);
    const h3 = field('Description', state.Description, margin, contentWidth);
    y += h3 + 8;

    const responsibilities = [];
    (state.positionRoles || []).forEach(role => {
        (role.Responsibilities || []).forEach(res => {
            if (res.ResponsibilityId) { responsibilities.push(res); }
        });
    });

    if (responsibilities.length > 0) {
        sectionHeading('Responsibilities');
        const innerX = margin + 12;
        const innerWidth = contentWidth - 16;

        responsibilities.forEach((res, index) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            const title = doc.splitTextToSize(responsibilityLabel(res.ResponsibilityId) || '-', innerWidth);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10.5);
            const sopTamilText = htmlToPlainText(getResponsibilityText(res, 'SopTamil', 'soptamil'));
            const sopEnglishText = htmlToPlainText(getResponsibilityText(res, 'SopEnglish', 'sopenglish'));
            const tamil = tamilTextImage(sopTamilText || '-', innerWidth, lineHeight);
            const englishLines = doc.splitTextToSize(sopEnglishText || '-', innerWidth);
            const cardHeight = 6 + title.length * lineHeight + 4 + (5 + tamil.height) + 3 + (5 + englishLines.length * lineHeight) + 4;

            ensureSpace(Math.min(cardHeight, bottomLimit - 18));
            const top = y;
            setFill(CARD);
            doc.roundedRect(margin, top, contentWidth, cardHeight, 2, 2, 'F');

            setFill(TEAL);
            doc.circle(margin + 6, top + 7.5, 3.4, 'F');
            setColor([255, 255, 255]);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.text(String(index + 1), margin + 6, top + 8.8, { align: 'center' });

            setColor(TEAL);
            doc.setFontSize(11);
            doc.text(title, innerX, top + 8.5);
            let cy = top + 6 + title.length * lineHeight + 4;

            setColor(MUTED);
            doc.setFontSize(8);
            doc.text('SOP TAMIL', innerX, cy + 1);
            if (tamil.data) { doc.addImage(tamil.data, 'PNG', innerX, cy + 2.5, innerWidth, tamil.height); }
            cy += 5 + tamil.height + 3;

            setColor(MUTED);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text('SOP ENGLISH', innerX, cy + 1);
            setColor(DARK);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10.5);
            doc.text(englishLines, innerX, cy + 5.5);

            y = top + cardHeight + 5;
        });
    }

    const totalPages = doc.internal.getNumberOfPages();
    const generated = new Date().toLocaleString('en-GB');
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        setFill(GOLD);
        doc.rect(margin, pageHeight - 14, contentWidth, 0.6, 'F');
        setColor(MUTED);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text('Chenthoor - Pure Veg  |  Generated on ' + generated, margin, pageHeight - 9);
        doc.text('Page ' + p + ' of ' + totalPages, pageWidth - margin, pageHeight - 9, { align: 'right' });
    }

    const fileName = 'Position_' + (state.PositionNo || state.Name || 'Details').toString().replace(/[^\w\-]+/g, '_') + '.pdf';
    return { blob: doc.output('blob'), fileName };
};
